// src/hooks/useSampleOrderOperations.js
// ✅ FIXED: build error (removed restoreStockForCancelledRequest import)
// ✅ STANDARDIZED: uses createdBy* only (no requestedBy* anywhere)
// ✅ Cloud Functions handle pricing + approval + stock restores
// ✅ Track deliveredAt timestamp when status changes to "Delivered"
// ✅ NEW: Creates follow-up task when status changes to "Delivered" (OPTIONAL; gated)

import { useState } from "react";
import {
  getFirestore,
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { submitSampleOrder } from "../utils/submitSampleOrder";
import { findDuplicateSampleRequest } from "../utils/sampleOrderGuardrails";
import {
  notifySampleOrderAssigned,
  notifySampleOrderCreated,
  notifySampleStatusChanged,
} from "../utils/notifications";

// 🔥 NEW: Import TaskService
import { TaskService } from "../components/tasks/TaskService";

export const useSampleOrderOperations = ({
  companyId,
  firebaseUser,
  userProfile,
  companySettings,
  inventory,
  accounts,
  companyUsers,
  canDecrementStock, // kept for compatibility (not used when CF manages stock)
  onReload,
  onError,
}) => {
  const db = getFirestore();
  const [saving, setSaving] = useState(false);

  // ✅ Controls who creates follow-up tasks:
  // - "cloud" (default): Cloud Function creates the task (recommended; respects admin timing)
  // - "client": client creates the task immediately on Delivered (fallback/testing only)
  const followUpTaskCreator = String(
    companySettings?.featureFlags?.followUpTaskCreator || "client"
  )
    .trim()
    .toLowerCase();

  // -----------------------------
  // Helpers (friendly labeling)
  // -----------------------------
  const safeStr = (v) => (v == null ? "" : String(v)).trim();
  const normalizeRole = (role) => safeStr(role).toLowerCase();
  const getTodayDateString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")}`;
  };

  const findCompanyUserByAnyId = (value) => {
    const target = safeStr(value);
    if (!target) return null;
    return (
      (companyUsers || []).find(
        (user) => safeStr(user?.id) === target || safeStr(user?.uid) === target
      ) || null
    );
  };

  const getAccountAssignedRepId = (account) =>
    safeStr(
      account?.assignedToId ||
        account?.assignedUserId ||
        account?.repUserId ||
        account?.salesRepId ||
        account?.userId
    );

  const getDefaultAssignedToId = (formAssignedToId, account) => {
    const explicitAssignedToId = safeStr(formAssignedToId);
    if (explicitAssignedToId) return explicitAssignedToId;

    const role = normalizeRole(userProfile?.role);
    if (role === "customer") {
      const repCandidate = getAccountAssignedRepId(account);
      const repUser = findCompanyUserByAnyId(repCandidate);
      return safeStr(repUser?.id || repUser?.uid || repCandidate);
    }

    if (role === "enduser") {
      const currentUser = findCompanyUserByAnyId(firebaseUser?.uid);
      return safeStr(currentUser?.id || currentUser?.uid || firebaseUser?.uid);
    }

    return "";
  };

  const getAccountDisplayName = (sampleOrderData, account) => {
    return (
      safeStr(sampleOrderData?.accountName) ||
      safeStr(account?.name) ||
      safeStr(account?.title) ||
      "Account"
    );
  };

  // If you have a human-friendly order number field, we’ll prefer it (NOT the Firestore doc id)
  const getFriendlyOrderNumber = (sampleOrderData) => {
    const candidates = [
      sampleOrderData?.orderNumber,
      sampleOrderData?.orderNo,
      sampleOrderData?.displayNumber,
      sampleOrderData?.requestNumber,
      sampleOrderData?.requestNo,
      sampleOrderData?.sampleOrderNumber,
    ]
      .map((x) => safeStr(x))
      .filter(Boolean);

    return candidates[0] || "";
  };

  const getProductSummary = (names) => {
    const clean = Array.from(new Set((names || []).map((n) => safeStr(n)).filter(Boolean)));
    if (!clean.length) return "";
    if (clean.length <= 2) return clean.join(", ");
    return `${clean.slice(0, 2).join(", ")} +${clean.length - 2}`;
  };

  // 🔥 OPTIONAL: Client-side task creator (GATED)
  const createFollowUpTask = async (sampleOrderId, sampleOrderData) => {
    // ✅ Default behavior: Cloud Function handles it (prevents duplicates + respects admin timing)
    if (followUpTaskCreator !== "client") {
      console.log(
        `[createFollowUpTask] Skipped (followUpTaskCreator=${followUpTaskCreator}). Cloud Function should create it.`
      );
      return null;
    }

    if (!companyId || !sampleOrderId || !sampleOrderData) {
      console.error("Missing required data for task creation");
      return null;
    }

    try {
      // Check if a follow-up task already exists for this sample order
      const tasksRef = collection(db, "companies", companyId, "tasks");
      const q = query(tasksRef, where("sampleOrderId", "==", sampleOrderId));
      const tasksSnap = await getDocs(q);

      if (!tasksSnap.empty) {
        console.log(`Follow-up task already exists for sample order ${sampleOrderId}`);
        return null;
      }

      // Get account details
      const account = (accounts || []).find((a) => a.id === sampleOrderData.accountId) || {};

      // Determine who to assign the task to
      let assignedUserIds = [];
      let assignedUser = null;

      // 1. Try the assigned person on the sample order
      if (sampleOrderData.assignedToId) {
        assignedUser = (companyUsers || []).find(
          (u) => u.id === sampleOrderData.assignedToId || u.uid === sampleOrderData.assignedToId
        );
      }

      // 2. If no assigned person, try the creator
      if (!assignedUser && sampleOrderData.createdBy) {
        assignedUser = (companyUsers || []).find(
          (u) => u.id === sampleOrderData.createdBy || u.uid === sampleOrderData.createdBy
        );
      }

      // 3. If still no one, use the current user (admin making the change)
      if (!assignedUser && firebaseUser?.uid) {
        assignedUser = (companyUsers || []).find((u) => u.uid === firebaseUser.uid);
      }

      if (assignedUser) {
        assignedUserIds = [assignedUser.uid || assignedUser.id];
      }

      // Get product details from items array
      let productDetails = "";
      let productNames = [];

      if (Array.isArray(sampleOrderData.items) && sampleOrderData.items.length > 0) {
        productDetails = sampleOrderData.items
          .map((item) => {
            const product = (inventory || []).find((inv) => inv.id === item.productId);
            const productName = product?.name || item.productName || "Unknown Product";
            const productCode = product?.sku || item.productCode || "No SKU";
            productNames.push(productName);
            return `- ${item.quantity || 1} × ${productName} (${productCode})`;
          })
          .join("\n");
      } else {
        // Fallback to legacy fields
        const product = (inventory || []).find((inv) => inv.id === sampleOrderData.productId);
        const productName = product?.name || sampleOrderData.productName || "Unknown Product";
        const productCode = product?.sku || sampleOrderData.productCode || "No SKU";
        productNames.push(productName);
        productDetails = `${sampleOrderData.quantity || 1} × ${productName} (${productCode})`;
      }

      // Calculate due date: 7 days from now (client fallback)
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      const dueDateString = dueDate.toISOString().split("T")[0];

      // Friendly labels
      const accountNameForTitle = getAccountDisplayName(sampleOrderData, account);
      const friendlyOrderNo = getFriendlyOrderNumber(sampleOrderData);
      const productSummary = getProductSummary(productNames);

      // Build comprehensive description (keep ID here, not in title)
      const description = `**Follow-up on Delivered Sample Order**

**Order Details:**
- Sample Order ID: ${sampleOrderId}
${friendlyOrderNo ? `- Order #: ${friendlyOrderNo}\n` : ""}- Account: ${accountNameForTitle}
- Contact: ${account.contactName || account.contact || account.email || "N/A"}
- Phone: ${account.phone || "N/A"}
- Email: ${account.email || "N/A"}

**Delivery Information:**
- Requested Delivery: ${sampleOrderData.requestedDate || "Not set"}
- Actual Delivery: ${new Date().toLocaleDateString()}
- Delivery Method: ${sampleOrderData.deliveryMethod || "Not specified"}
- Tracking: ${sampleOrderData.trackingNumber || "None"}

**Products Delivered:**
${productDetails}

**Follow-up Instructions:**
1. Contact the account to confirm receipt
2. Inquire about product satisfaction
3. Ask about potential orders
4. Document any feedback or issues

**Account Address:**
${account.addressStreet || account.address || "N/A"}
${account.addressCity || ""}, ${account.addressState || ""} ${account.addressZip || ""}

**Notes:**
${sampleOrderData.notes || "No additional notes provided."}`;

      // ✅ Clean, user-friendly title (NO Firestore doc ID)
      const title = `Follow-up: ${accountNameForTitle}${productSummary ? ` — ${productSummary}` : ""}`;

      // Create the task
      const taskPayload = {
        title,
        description,
        status: "To Do",
        priority: "Medium",
        type: "Follow-up",
        dueDate: dueDateString,
        dueTime: "09:00",
        recurrence: "None",
        accountId: sampleOrderData.accountId || "",
        estimatedHours: 1,

        attachments: [],

        assignMode: "users",
        assignedToUserIds: assignedUserIds,
        soloCopies: false,
        createdByUid: firebaseUser?.uid || null,
        createdByRole: userProfile?.role || "endUser",

        sampleOrderId: sampleOrderId,
        sampleOrderStatus: "Delivered",
        deliveredAt: new Date().toISOString(),

        productNames: productNames,
      };

      const result = await TaskService.createTask(companyId, taskPayload);
      console.log("✅ Created follow-up task for delivered sample order:", {
        sampleOrderId,
        taskId: result.createdTaskIds?.[0],
        assignedTo: assignedUserIds,
      });

      // Update the sample order with task reference
      const orderRef = doc(db, "companies", companyId, "sampleOrders", sampleOrderId);
      await updateDoc(orderRef, {
        followUpTaskId: result.createdTaskIds?.[0],
        followUpTaskCreated: serverTimestamp(),
      });

      return result.createdTaskIds?.[0];
    } catch (error) {
      console.error("❌ Failed to create follow-up task:", error);
      return null;
    }
  };

  const createSampleOrder = async (form, options, sampleOrders) => {
    console.log("[createSampleOrder] Starting creation...");

    if (!companyId) {
      const error = "Missing companyId - cannot create sample order";
      console.error("[createSampleOrder] ❌", error);
      onError(error);
      return;
    }

    if (!firebaseUser) {
      const error = "Not authenticated - cannot create sample order";
      console.error("[createSampleOrder] ❌", error);
      onError(error);
      return;
    }

    if (userProfile?.companyId !== companyId) {
      const error = `User companyId mismatch. Profile: ${userProfile?.companyId}, Expected: ${companyId}`;
      console.error("[createSampleOrder] ❌", error);
      onError("Permission denied: Company ID mismatch");
      return;
    }

    const rawItems =
      Array.isArray(form.items) && form.items.length > 0
        ? form.items
        : [{ productId: form.productId, quantity: form.quantity }];

    const validItems = rawItems.filter((i) => i && i.productId && Number(i.quantity) > 0);

    if (validItems.length === 0) {
      onError("Please add at least one product to sample.");
      return;
    }

    const maxItemsPerOrder = companySettings?.featureFlags?.maxItemsPerOrder;
    if (typeof maxItemsPerOrder === "number" && validItems.length > maxItemsPerOrder) {
      onError(
        `Cannot submit order. Maximum ${maxItemsPerOrder} items allowed per order. You have ${validItems.length} items.`
      );
      return;
    }

    if (!form.requestedDate) {
      onError("Please choose a valid requested delivery date.");
      return;
    }

    onError("");
    setSaving(true);

    try {
      const isNewCustomer = options?.isNewCustomer;
      const newCustomer = options?.newCustomer;
      const staffGroupId = companySettings?.staffGroupId || null;
      const requireReasonForSample = !!companySettings?.featureFlags?.requireReasonForSample;
      const duplicateBlockEnabled = !!companySettings?.featureFlags?.blockDuplicateRequestsEnabled;
      const duplicateBlockDays = companySettings?.featureFlags?.blockDuplicateDays;

      let accountId = form.accountId || null;
      let accountName = form.accountName || null;
      let accountGroupIds = [];

      if (isNewCustomer) {
        console.log("[createSampleOrder] Creating new customer account...");

        if (
          !newCustomer ||
          !newCustomer.name ||
          !newCustomer.accountName ||
          !newCustomer.email ||
          !newCustomer.shippingAddress ||
          !newCustomer.phone
        ) {
          throw new Error("New customer information is incomplete. Please fill all required fields.");
        }

        const accountsRef = collection(db, "companies", companyId, "accounts");
        const defaultAssignedToId = getDefaultAssignedToId(form.assignedToId, null) || firebaseUser.uid;

        accountGroupIds = staffGroupId ? [staffGroupId] : getEffectiveGroupIds(userProfile, companySettings);

        const newAccPayload = {
          name: newCustomer.accountName,
          contact: newCustomer.name,
          primaryContact: {
            name: newCustomer.name,
            email: newCustomer.email,
            phone: newCustomer.phone,
          },
          email: newCustomer.email,
          phone: newCustomer.phone,
          shippingAddress: newCustomer.shippingAddress,
          address: {
            street: newCustomer.addressStreet || null,
            city: newCustomer.addressCity || null,
            state: newCustomer.addressState || null,
            zip: newCustomer.addressZip || null,
          },
          createdAt: serverTimestamp(),
          createdBy: firebaseUser.uid,
          assignedToId: defaultAssignedToId,
          assignedUserId: defaultAssignedToId,
          groupIds: Array.isArray(accountGroupIds) ? accountGroupIds.filter(Boolean).slice(0, 10) : [],
          source: "sampleRequestForm",
          active: true,
          companyId,
        };

        const newAccRef = await addDoc(accountsRef, newAccPayload);
        accountId = newAccRef.id;
        accountName = newCustomer.accountName;

        console.log("[createSampleOrder] ✅ Created new account:", accountId);

        await onReload.accounts();
      } else if (accountId) {
        const selectedAccount = accounts.find((acc) => acc.id === accountId);
        accountName = selectedAccount?.name || accountName;
        accountGroupIds = Array.isArray(selectedAccount?.groupIds)
          ? selectedAccount.groupIds.filter(Boolean).slice(0, 10)
          : [];
      }

      if (!accountId) throw new Error("Please select a customer/account.");
      if (!accountGroupIds.length && staffGroupId) accountGroupIds = [staffGroupId];

      const trimmedNotes = String(form.notes || "").trim();
      if (requireReasonForSample && !trimmedNotes) {
        throw new Error("A reason for this sample is required before submitting.");
      }

      if (duplicateBlockEnabled && !isNewCustomer && accountId) {
        const duplicate = findDuplicateSampleRequest({
          sampleOrders,
          accountId,
          productIds: validItems.map((item) => item.productId),
          windowDays: duplicateBlockDays,
        });

        if (duplicate) {
          const duplicateNames =
            duplicate.duplicateItemNames.length > 0
              ? duplicate.duplicateItemNames.join(", ")
              : "one or more of these items";
          throw new Error(
            `This account already requested ${duplicateNames} within the last ${duplicateBlockDays} days.`
          );
        }
      }

      const createdByName =
        userProfile?.fullName || firebaseUser.displayName || firebaseUser.email || null;

      const createdByRole = (userProfile?.role || "customer").toString().trim().toLowerCase();

      const selectedAccount = (accounts || []).find((acc) => acc.id === accountId) || null;
      const assignedToId = getDefaultAssignedToId(form.assignedToId, selectedAccount) || null;
      const assignedUser = assignedToId ? findCompanyUserByAnyId(assignedToId) : null;
      const assignedToName = assignedUser?.fullName || assignedUser?.email || null;

      const groupMode = options?.groupMode || form.groupMode || "single";
      const kitId = options?.kitId || form.kitId || null;

      const initialStatus = "Pending";

      const submitResult = await submitSampleOrder({
        db,
        companyId,

        createdBy: firebaseUser.uid,
        createdByName,
        createdByRole,

        accountId,
        accountName,
        assignedToId,
        assignedToName,

        requestedDate: form.requestedDate,
        deliveryDate: form.deliveryDate || null,
        notes: trimmedNotes || null,

        rawItems: validItems,
        inventory,

        groupMode,
        kitId,

        groupIds: accountGroupIds,
        accountGroupIds,

        initialStatus,
      });

      const createdRequestId =
        (submitResult && typeof submitResult === "object" && submitResult.id) ||
        (typeof submitResult === "string" ? submitResult : null);

      if (createdRequestId) {
        await notifySampleOrderCreated({
          companyId,
          sampleRequestId: createdRequestId,
          sampleRequest: {
            accountId,
            accountName,
            productName: form?.productName || null,
            items: validItems,
          },
          companyUsers,
          assignedUserId: assignedToId || null,
          creatorUserId: firebaseUser.uid,
          creatorName: createdByName || firebaseUser.email || "Someone",
        });
      }

      await Promise.all([onReload.sampleOrders(), onReload.accounts(), onReload.inventory()]);

      console.log("[createSampleOrder] ✅ Sample order created successfully");
      return true;
    } catch (err) {
      console.error("❌ Error creating sample order:", err);
      onError(err?.message || "Could not create sample order.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateSampleOrder = async (requestId, updates, sampleOrders, isAdmin) => {
    if (!companyId) return;

    if (!isAdmin) {
      onError("You don't have permission to update sample requests.");
      return;
    }

    const existing = (sampleOrders || []).find((r) => r.id === requestId) || null;
    const prevStatus = existing?.status || null;
    const prevAssignedToId = existing?.assignedToId || null;
    const nextStatus = updates?.status || existing?.status || "Pending";
    const nextAssignedToId =
      (Object.prototype.hasOwnProperty.call(updates || {}, "assignedToId")
        ? updates.assignedToId
        : existing?.assignedToId) || null;
    const requiresAssigneeAfterApproval = [
      "Approved",
      "Preparation",
      "Shipped",
      "Delivered",
      "Follow Up",
      "Completed",
      "Converted to Order",
    ].includes(nextStatus);

    try {
      if (requiresAssigneeAfterApproval && !nextAssignedToId) {
        throw new Error("Assign a representative before moving a sample request into or beyond Approved.");
      }

      const requestRef = doc(db, "companies", companyId, "sampleOrders", requestId);

      const payload = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // ✅ Delivered tracking (for server auto-transition timers)
      if (updates.status === "Delivered" && prevStatus !== "Delivered") {
        payload.deliveredAt = serverTimestamp();
        payload.deliveryDate =
          (typeof updates?.deliveryDate === "string" && updates.deliveryDate.trim()) ||
          existing?.deliveryDate ||
          getTodayDateString();

        // ✅ IMPORTANT:
        // If followUpTaskCreator === "cloud", do NOT create the task here (Cloud Function will).
        // If followUpTaskCreator === "client", create it immediately (fallback/testing).
        if (followUpTaskCreator === "client") {
          const combinedData = { ...existing, ...updates };
          try {
            await createFollowUpTask(requestId, combinedData);
          } catch (taskErr) {
            console.error("Failed to create follow-up task:", taskErr);
          }
        }
      }

      if (updates.status && updates.status !== "Delivered" && prevStatus === "Delivered") {
        payload.previouslyDeliveredAt = existing?.deliveredAt || null;
        payload.deliveredAt = null;
      }

      if (updates.status && existing && updates.status !== existing.status) {
        payload.statusUpdatedBy = firebaseUser?.uid || null;
        payload.statusUpdatedAt = serverTimestamp();
      }

      // ✅ Optional: Cancellation audit fields (server handles stock restore)
      if (updates.status === "Cancelled" && prevStatus !== "Cancelled") {
        payload.cancelledAt = serverTimestamp();
        payload.cancelledBy = firebaseUser?.uid || null;
      }

      await updateDoc(requestRef, payload);

      // Assignment notifications
      if (
        Object.prototype.hasOwnProperty.call(updates || {}, "assignedToId") &&
        updates.assignedToId &&
        updates.assignedToId !== prevAssignedToId
      ) {
        const assignedByName =
          userProfile?.fullName || firebaseUser?.displayName || firebaseUser?.email || "Someone";

        const nextForNotif = { ...(existing || {}), ...(updates || {}) };

        await notifySampleOrderAssigned({
          companyId,
          sampleRequestId: requestId,
          sampleRequest: nextForNotif,
          assignedUserId: updates.assignedToId,
          assignedByName,
        });
      }

      // Status change notifications
      if (updates.status && existing && updates.status !== existing.status) {
        const currentUserId = firebaseUser?.uid || null;

        const nextAssignedToId =
          (Object.prototype.hasOwnProperty.call(updates || {}, "assignedToId")
            ? updates.assignedToId
            : existing?.assignedToId) || null;

        const potentialRecipients = [existing?.createdBy, nextAssignedToId].filter(Boolean);
        const recipientUserIds = Array.from(new Set(potentialRecipients.filter((uid) => uid !== currentUserId)));

        if (recipientUserIds.length > 0) {
          const nextForNotif = { ...(existing || {}), ...(updates || {}) };

          await notifySampleStatusChanged({
            companyId,
            sampleRequestId: requestId,
            sampleRequest: nextForNotif,
            oldStatus: prevStatus,
            newStatus: updates.status,
            recipientUserIds,
            companyUsers,
            actorUserId: currentUserId,
          });
        }
      }

      await Promise.all([onReload.sampleOrders(), onReload.inventory()]);
    } catch (err) {
      console.error("Error updating request:", err);
      onError(err?.message || "Failed to update sample request.");
    }
  };

  return {
    saving,
    createSampleOrder,
    updateSampleOrder,

    // Keep exposed for manual testing, but default mode skips unless feature flag enables "client"
    createFollowUpTask,
  };
};

const getEffectiveGroupIds = (profile, companySettings) => {
  const normalizeRoleLocal = (role) => String(role || "").trim().toLowerCase();
  const role = normalizeRoleLocal(profile?.role);
  const staffGroupId = companySettings?.staffGroupId || null;
  const base = Array.isArray(profile?.groupIds) ? profile.groupIds.filter(Boolean) : [];

  const isStaffRole = (r) => {
    const normalized = normalizeRoleLocal(r);
    return normalized === "admin" || normalized === "owner" || normalized === "enduser";
  };

  if (isStaffRole(role) && staffGroupId && !base.includes(staffGroupId)) {
    return [staffGroupId, ...base].slice(0, 10);
  }

  return base.slice(0, 10);
};
